
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { VinResults } from "./VinResults";
import { VinLookupRecord } from "./VinLookupRecord";
import { X, Search } from "lucide-react";

interface VinLookup {
  id: string;
  vin: string;
  vehicle_data: any;
  raw_dataone_response: any;
  created_at: string;
}

interface VinLookupHistoryProps {
  lookups: VinLookup[];
  onDelete?: (id: string) => void;
}

export const VinLookupHistory = ({ lookups, onDelete }: VinLookupHistoryProps) => {
  const [selectedLookup, setSelectedLookup] = useState<VinLookup | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isResultsOpen, setIsResultsOpen] = useState(false);

  const filteredLookups = lookups.filter((lookup) => {
    const searchLower = searchQuery.toLowerCase();
    
    // Search in VIN
    if (lookup.vin.toLowerCase().includes(searchLower)) return true;
    
    // Search in vehicle data only if it exists
    if (lookup.vehicle_data && Array.isArray(lookup.vehicle_data)) {
      return lookup.vehicle_data.some((item: any) => {
        const value = item.Value?.toString().toLowerCase() || "";
        return value.includes(searchLower);
      });
    }
    
    return false;
  });

  const handleViewLookup = (lookup: VinLookup) => {
    setSelectedLookup(lookup);
    setIsResultsOpen(true);
  };

  const handleCloseResults = () => {
    setIsResultsOpen(false);
    setSelectedLookup(null);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>VIN Lookup History</CardTitle>
          <div className="mt-4 relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search VIN or vehicle details..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                  onClick={() => setSearchQuery("")}
                >
                  <X className="h-4 w-4 text-gray-500" />
                  <span className="sr-only">Clear search</span>
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="divide-y divide-gray-200">
            {filteredLookups.length === 0 ? (
              <div className="py-6 text-center text-sm text-gray-500">
                {searchQuery ? "No results found" : "No VIN lookups yet"}
              </div>
            ) : (
              filteredLookups.map((lookup) => (
                <VinLookupRecord
                  key={lookup.id}
                  lookup={lookup}
                  onDelete={onDelete}
                  onView={() => handleViewLookup(lookup)}
                />
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {selectedLookup && isResultsOpen && (
        <VinResults
          nhtsaData={selectedLookup.vehicle_data?.Results?.[0] || null}
          dataOneData={selectedLookup.raw_dataone_response || null}
          onClose={handleCloseResults}
          isOpen={isResultsOpen}
        />
      )}
    </div>
  );
};
